import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useDebouncedCallback } from 'use-debounce';

import { CarouselItemPropTypes } from './CarouselItem';
import SlideButton, { SlideButtonType } from './SlideButton';

export interface CarouselPropTypes {
  children: ReactElement<CarouselItemPropTypes>[];
  numberItemsToShow?: 1 | 3 | 5;
  className?: string;
  overflow?: boolean;
  sliderButtonBackClassName?: string;
  sliderButtonNextClassName?: string;
}

const Carousel = ({
  children,
  numberItemsToShow = 5,
  className,
  overflow = false,
  sliderButtonBackClassName,
  sliderButtonNextClassName,
}: CarouselPropTypes): ReactElement => {
  // Ensures that TW will include the correct classNames when bundled.
  const mapItemsToClassNames = {
    1: 'w-full',
    3: 'w-1/3',
    5: 'w-1/5',
  };

  const [currentPage, setCurrentPage] = useState<number>(0);
  const length = children.length;

  useEffect(() => {
    setCurrentPage(0);
  }, []);

  const isNextDisabled = useMemo(() => {
    return (currentPage + 1) * numberItemsToShow >= length;
  }, [length, currentPage]);

  const isPrevDisabled = useMemo(() => {
    return currentPage === 0;
  }, [currentPage]);

  const next = () => {
    setCurrentPage((prevState) => prevState + 1);
  };

  const prev = () => {
    setCurrentPage((prevState) => prevState - 1);
  };

  return (
    <div className="carousel-wrapper flex w-full relative">
      {!isPrevDisabled && (
        <SlideButton
          className={twMerge(
            'absolute left-0 bottom-1/2 transform translate-y-1/2 z-10',
            sliderButtonBackClassName
          )}
          type={SlideButtonType.BACK}
          onClick={prev}
        />
      )}
      <div
        className={twMerge(
          'carousel w-full h-full',
          overflow ? 'overflow-visible fadeEdges' : 'overflow-hidden'
        )}
      >
        <div
          className={twMerge(
            className,
            `transition-transform whitespace-nowrap gap-x-3`
          )}
          style={{ transform: `translateX(-${currentPage * 100}%)` }}
          data-testid="carousel-content"
        >
          {React.Children.map(children, (child, index) => {
            const childClassName = child.props.className;
            const isVisible =
              index >= currentPage * numberItemsToShow &&
              index < (currentPage + 1) * numberItemsToShow;
            const width = mapItemsToClassNames[numberItemsToShow];

            return React.cloneElement(child, {
              className: twMerge(childClassName, width),
              isVisible,
            });
          })}
        </div>
      </div>
      {!isNextDisabled && (
        <SlideButton
          className={twMerge(
            'absolute right-0 bottom-1/2 transform translate-y-1/2',
            sliderButtonNextClassName
          )}
          type={SlideButtonType.NEXT}
          onClick={next}
        />
      )}
    </div>
  );
};

export default Carousel;
